/**
 * @spec docs/specs/features/staking-position-ui.md#wallet-integration
 * Wallet status button that opens the wallet drawer
 */
import { cn } from '@/lib/cn';
import { useSelectedWallet } from '@/hooks/useSelectedWallet';
import { WalletAddress } from '@/components/ui/WalletAddress';
import { AddressAvatar } from '@/components/ui/AddressAvatar';
import { Icon } from '@/components/ui/icons';

export interface WalletStatusProps {
  /** Custom className for the container */
  className?: string;
  /** Callback to open the wallet drawer */
  onOpenDrawer?: () => void;
  /** Whether the wallet drawer is currently open */
  isDrawerOpen?: boolean;
}

/**
 * Displays the connected wallet address as a button that opens the wallet drawer
 */
export function WalletStatus({ className, onOpenDrawer, isDrawerOpen }: WalletStatusProps) {
  const { address } = useSelectedWallet();

  if (!address) {
    return null;
  }

  return (
    <button
      onClick={onOpenDrawer}
      className={cn(
        'flex h-9 items-center gap-2 rounded-full pl-1.5 pr-2.5',
        'bg-white/[0.05] ring-1 ring-inset ring-white/[0.08]',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-300',
        isDrawerOpen
          ? 'pointer-events-none'
          : 'transition-colors hover:bg-white/[0.08] hover:ring-white/[0.14]',
        className
      )}
      aria-haspopup="dialog"
      aria-expanded={!!isDrawerOpen}
      type="button"
    >
      <AddressAvatar address={address} size={24} />
      {/* The accessible name keeps the visible address (WCAG 2.5.3, label in name). */}
      <span className="sr-only">Wallet </span>
      <WalletAddress address={address} className="text-[13px] font-bold text-text-primary" />
      <span className="sr-only">, open wallet menu</span>
      <Icon name="chevron-down" size={14} className="text-text-muted" />
    </button>
  );
}
